// @ts-check

import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  CAMPAIGN_NAME_MAX_LENGTH,
  CAMPAIGN_NAME_MIN_LENGTH,
  ERROR_CAMPAIGN_NAME_MAX_LENGTH,
  ERROR_CAMPAIGN_NAME_MIN_LENGTH,
} from 'core';
import { Modal } from 'core/components/Modal';
import { ErrorValidation } from '../ErrorValidation';
import { Tooltip } from '../Tooltip';

/**
 * @typedef {'confirm' | 'name'} CloneModalStep
 */

/**
 * @param {{
 *  isOpen: boolean;
 *  onClose: () => void;
 *  campaignName?: string | null;
 *  sourceIncludesFile?: boolean;
 *  onCloneAndRefresh?: (cloneName: string) => (void | Promise<void>);
 * }} props
 */
// This is a multi-step component that manages the flow of cloning a campaign,
// including confirmation and naming. It also ensures validation of the campaign name.
function CloneCampaignFlowModal({
  isOpen,
  onClose,
  campaignName,
  sourceIncludesFile = false,
  onCloneAndRefresh,
}) {
  // Guard for async submit flows: prevents calling setState after the modal
  // unmounts (e.g. user navigates away while a clone request is in-flight).
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /** @type {[CloneModalStep, React.Dispatch<React.SetStateAction<CloneModalStep>>]} */
  const [step, setStep] = useState(/** @type {CloneModalStep} */ ('confirm'));
  const [cloneName, setCloneName] = useState('');
  const [cloneNameTouched, setCloneNameTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resolvedCampaignName = campaignName || '';

  const defaultCloneName = useMemo(() => {
    const trimmed = resolvedCampaignName.trim();

    // Note: prepending "COPY " may cause cloned name to exceed 60 char max length.
    // Business rule is to trigger validation in this case; force user to shorten name.
    return trimmed ? `COPY ${trimmed}` : '';
  }, [resolvedCampaignName]);

  useEffect(() => {
    if (!isOpen) {
      setStep('confirm');
      setCloneName('');
      setCloneNameTouched(false);
      setIsSubmitting(false);
      return;
    }

    // Initialize the name only when opening, but don't clobber user edits.
    setCloneName((prev) => prev || defaultCloneName);
  }, [isOpen, defaultCloneName]);

  const showFileNote = Boolean(sourceIncludesFile);

  const cloneNameTrimmedLength = String(cloneName).trim().length;
  const cloneNameError = useMemo(() => {
    if (!cloneNameTouched) return '';
    if (cloneNameTrimmedLength < CAMPAIGN_NAME_MIN_LENGTH) {
      return ERROR_CAMPAIGN_NAME_MIN_LENGTH;
    }
    if (cloneNameTrimmedLength > CAMPAIGN_NAME_MAX_LENGTH) {
      return ERROR_CAMPAIGN_NAME_MAX_LENGTH;
    }
    return '';
  }, [cloneNameTouched, cloneNameTrimmedLength]);

  const canSubmitClone =
    cloneNameTrimmedLength >= CAMPAIGN_NAME_MIN_LENGTH &&
    cloneNameTrimmedLength <= CAMPAIGN_NAME_MAX_LENGTH &&
    !cloneNameError;

  const canSubmitCloneAndRefresh =
    canSubmitClone && Boolean(onCloneAndRefresh) && !isSubmitting;

  // Validate the name immediately when entering the naming step, if the
  // default/pre-filled name is already invalid.
  useEffect(() => {
    // If the default/pre-filled clone name is already invalid (most commonly
    // due to exceeding the max length after prepending "COPY"), surface the
    // validation immediately when the naming step is shown.
    if (!isOpen) return;
    if (step !== 'name') return;
    if (cloneNameTouched) return;

    if (
      cloneNameTrimmedLength > CAMPAIGN_NAME_MAX_LENGTH ||
      (cloneNameTrimmedLength > 0 &&
        cloneNameTrimmedLength < CAMPAIGN_NAME_MIN_LENGTH)
    ) {
      setCloneNameTouched(true);
    }
  }, [cloneNameTouched, cloneNameTrimmedLength, isOpen, step]);

  return (
    <Modal
      isOpen={isOpen}
      title="Clone campaign"
      maxWidth="600px"
      padding="24px"
      hideHeaderBorder
      scrollBody
      onClose={onClose}
    >
      {step === 'confirm' ? (
        <>
          <div className="mt-3">
            <p className="mb-0">
              Do you want to clone{' '}
              {resolvedCampaignName ? (
                <strong>{resolvedCampaignName}</strong>
              ) : (
                <strong>this campaign</strong>
              )}
              ?
            </p>
            {showFileNote ? (
              <p className="mt-2 mb-0 text-muted">
                <em>
                  <strong>Please note:</strong> Uploaded files will not be
                  included in the cloned campaign.
                </em>
              </p>
            ) : null}
          </div>
          <div className="mt-4 d-flex justify-content-between">
            <button
              type="button"
              className="btn btn-md btn-gray"
              data-ga-event="link"
              data-ga-action="click"
              data-ga-name="onclick"
              data-ga-type="cta"
              data-ga-region="main content"
              data-ga-section="Clone Campaign Modal"
              data-ga-component=""
              data-ga-text="Cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-md btn-maroon"
              data-ga-event="link"
              data-ga-action="click"
              data-ga-name="onclick"
              data-ga-type="cta"
              data-ga-region="main content"
              data-ga-section="Clone Campaign Modal"
              data-ga-component=""
              data-ga-text="Yes, clone campaign"
              onClick={() => {
                setStep('name');
              }}
            >
              Yes, clone campaign
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="mt-2">
            <div className="d-flex align-items-center gap-2 mb-2">
              <div className="fw-bold">Name cloned campaign</div>
              <Tooltip tooltipId="clone-campaign-name-tooltip">
                The name given to the campaign, potentially public; minimum 5
                characters.
              </Tooltip>
            </div>
            <input
              type="text"
              className={`form-control ${cloneNameError ? 'is-invalid' : ''}`}
              aria-describedby="error-clone-campaign-name"
              value={cloneName}
              disabled={isSubmitting}
              onChange={(e) => {
                if (!cloneNameTouched) setCloneNameTouched(true);
                setCloneName(e.target.value);
              }}
              onBlur={() => {
                setCloneNameTouched(true);
              }}
            />
            <ErrorValidation
              id="error-clone-campaign-name"
              errors={cloneNameError ? [cloneNameError] : []}
            />
          </div>
          <div className="mt-4 d-flex justify-content-between">
            <button
              type="button"
              className="btn btn-md btn-gray"
              disabled={isSubmitting}
              data-ga-event="link"
              data-ga-action="click"
              data-ga-name="onclick"
              data-ga-type="cta"
              data-ga-region="main content"
              data-ga-section="Clone Campaign Modal"
              data-ga-component=""
              data-ga-text="Back"
              onClick={() => {
                setStep('confirm');
              }}
            >
              Back
            </button>
            <button
              type="button"
              className="btn btn-md btn-maroon"
              disabled={!canSubmitCloneAndRefresh}
              data-ga-event="link"
              data-ga-action="click"
              data-ga-name="onclick"
              data-ga-type="cta"
              data-ga-region="main content"
              data-ga-section="Clone Campaign Modal"
              data-ga-component=""
              data-ga-text="Clone and refresh campaign"
              onClick={async () => {
                if (!onCloneAndRefresh) return;
                try {
                  setIsSubmitting(true);
                  await onCloneAndRefresh(cloneName.trim());
                } finally {
                  if (isMountedRef.current) {
                    setIsSubmitting(false);
                  }
                }
              }}
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Cloning...
                </>
              ) : (
                'Clone and refresh campaign'
              )}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

export { CloneCampaignFlowModal };
